import React from "react";
import { Drawer, Avatar, Pill, VbButton } from "@/components/vb";
import { ChevronRight, ArrowRight } from "lucide-react";
import { renderMarkdownSafe } from "@/lib/markdown";
import { providerMeta } from "@/pages/vb/run/providerMeta";
import { SignalResponseModal } from "./SignalResponseModal";
import {
  signalStatus,
  statusTone,
  modelFamily,
  plainExcerpt,
  signalFinding,
  type SignalStatus,
} from "./signalCardUtils";

type Citation = { id?: number; url?: string | null; response?: string | null };
type TechnicalSignal = {
  id?: number;
  model_used?: string;
  response?: string | null;
  citations?: Citation[];
};

type Props = {
  /** The active signal, or null when the drawer is closed. */
  signal: any | null;
  onClose: () => void;
  /** Tier label + position, for the eyebrow ("Technical · Signal 1 of 14"). */
  tierLabel?: string;
  index?: number;
  total?: number;
  /** Formal signal names (definition.name) that have an actual fix in the
   *  Workbench queue. "Start in Workbench" only shows for signals in this set —
   *  strong/healthy signals (and any without a curated accelerator) have no fix. */
  workbenchFixNames: Set<string>;
  /** Navigate to the Workbench for this report. */
  onStartWorkbench: () => void;
};

const SCORE_LABEL: Record<SignalStatus, string> = {
  pass: "Healthy",
  watch: "Issue",
  fail: "Critical",
  unknown: "—",
};

/** AI Trust Signal detail drawer (AITS-492): the accordion body, re-homed into a
 *  focus-trapped right drawer — what we found, why it matters, what each AI said
 *  (with a full-response dialog), and what to do. Reuses the shared Radix `Drawer`
 *  (re-whitened; `.vb-root` otherwise greys the panel). */
export function SignalDrawer({
  signal,
  onClose,
  tierLabel,
  index,
  total,
  workbenchFixNames,
  onStartWorkbench,
}: Props) {
  const [respTs, setRespTs] = React.useState<TechnicalSignal | null>(null);

  // Keep the last signal during the slide-out so the panel isn't blank.
  const [shown, setShown] = React.useState<any | null>(signal);
  React.useEffect(() => {
    if (signal) setShown(signal);
  }, [signal]);

  const open = signal != null;
  const sig = shown;

  const status = signalStatus(sig?.score, sig?.scoring_criteria);
  const scoreText = sig?.score ? String(sig.score) : SCORE_LABEL[status];
  // Whatever the card teased, rendered here in full (see signalFinding). Free-
  // shortened reports and partial runs have no summary, so `isFinding` is false
  // and the heading follows the generic copy the card fell back to.
  const finding = signalFinding(sig);
  const whyMatters = (sig?.why_matters ?? "").trim();
  const recommendation = (sig?.recommendation ?? "").trim();
  const technicalSignals: TechnicalSignal[] = sig?.technical_signals ?? [];
  // Only offer the Workbench when this signal is actually in the fix queue.
  const hasWorkbenchFix = sig?.name != null && workbenchFixNames.has(sig.name);

  const eyebrowBits = [
    tierLabel,
    typeof index === "number" && typeof total === "number" ? `Signal ${index + 1} of ${total}` : null,
  ].filter(Boolean);

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        widthClassName="w-full sm:w-[580px] sm:max-w-[96vw]"
        eyebrow={eyebrowBits.length ? eyebrowBits.join(" · ") : undefined}
        title={sig?.name ?? "AI Trust Signal"}
        srTitle={sig?.name ?? "AI Trust Signal"}
        description={
          <span className="vb-sig-scoreline">
            <Pill tone={statusTone(status)}>{scoreText}</Pill>
          </span>
        }
        footer={
          <div className="vb-sig-drawer-ft">
            {hasWorkbenchFix && (
              <VbButton variant="primary" className="mr-auto" onClick={onStartWorkbench}>
                Start in Workbench
                <ArrowRight className="h-[14px] w-[14px]" />
              </VbButton>
            )}
            <VbButton variant="ghost" onClick={onClose}>
              Close
            </VbButton>
          </div>
        }
      >
        {finding.text && (
          <section className="vb-sig-sec">
            <h4 className="vb-sig-sec__h">
              {finding.isFinding ? "What we found" : "What this signal measures"}
            </h4>
            <div className="vb-md" dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(finding.text) }} />
          </section>
        )}

        {whyMatters && (
          <section className="vb-sig-sec">
            <h4 className="vb-sig-sec__h">Why this matters</h4>
            <div className="vb-md" dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(whyMatters) }} />
          </section>
        )}

        <section className="vb-sig-sec">
          <h4 className="vb-sig-sec__h">What each AI literally said</h4>
          {technicalSignals.length ? (
            <div className="vb-sig-aiq-list">
              {technicalSignals.map((ts, i) => {
                const family = modelFamily(ts.model_used);
                const label = providerMeta(family).label;
                const preview = plainExcerpt(ts.response, 200);
                return (
                  <div className="vb-sig-aiq" key={ts.id ?? i}>
                    <div className="vb-sig-aiq__top">
                      <Avatar model={family} size="sm" decorative />
                      {label}
                    </div>
                    {preview && <div className="vb-sig-aiq__sum">{preview}</div>}
                    <button type="button" className="vb-sig-aiq__trigger" onClick={() => setRespTs(ts)}>
                      See full response
                      <ChevronRight className="h-[13px] w-[13px]" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="vb-sig-aiq__empty">No model summaries available for this signal.</p>
          )}
        </section>

        {recommendation && (
          <section className="vb-sig-sec">
            <h4 className="vb-sig-sec__h">What to do</h4>
            <div className="vb-md" dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(recommendation) }} />
          </section>
        )}
      </Drawer>

      <SignalResponseModal open={respTs != null} onOpenChange={(o) => !o && setRespTs(null)} ts={respTs} />
    </>
  );
}

export default SignalDrawer;
